import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router";
import { useBatch, useReleaseBatch, useReprocessBatch, useDeleteBatch } from "../hooks/useBatches";
import { ReconciliationPanel } from "../components/ReconciliationPanel";
import { TransactionsPanel } from "../components/TransactionsPanel";
import { BatchCheckInModal } from "../components/BatchCheckInModal";
import {
  BatchStatusBadge,
  BatchStageBreakdown,
  DocStateBadge,
  workItemStatus,
} from "../components/BatchStatusBadge";
import { DocErrorPopover } from "../components/DocErrorPopover";
import { MoveStepModal } from "../components/MoveStepModal";
import { computeBatchProgress } from "../lib/batch-progress";
import { ConfirmModal } from "@/components/ConfirmModal";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { ComposeNoteDialog } from "@/features/directed-notes/components/ComposeNoteDialog";
import { canSendDirectNote } from "@/features/directed-notes/lib/permissions";
import { toast } from "@/components/Toaster";
import { api } from "@/lib/api-client";
import { getApiErrorMessage } from "@/lib/error-toast";
import {
  formatStep,
  hasStructuredDisplayId,
  renderBatchLabel,
  renderDocumentLabel,
} from "@/lib/formatters";
import { useTenantDisplayTemplates } from "@/hooks/useTenantDisplayTemplates";
import { usePoll } from "@/hooks/usePoll";
import { useWsClient } from "@/lib/ws-provider";
import { isClaimedDocStatus, deriveDocState } from "@capture/shared";
import "../batches.css";

export function BatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { batch, isLoading, isRefreshing, error, refresh } = useBatch(id, useWsClient());
  // Live-batch-surfaces Task 4 — primary freshness is the WS refetch inside
  // useBatch (BATCH_PROGRESS on this batch's own batch:<id> channel + the
  // tenant-wide batch-mutating events), replacing R-159's 8s short-poll as
  // the "watch a batch progress" mechanism. The poll stays as a reconnect
  // safety net at 30s; still paused when the tab is hidden and still fires
  // immediately on tab return.
  usePoll(refresh, 30_000);
  const { release, isSubmitting: isReleasing } = useReleaseBatch();
  const { reprocess, isSubmitting: isReprocessing } = useReprocessBatch();
  const { deleteBatch, isSubmitting: isDeleting } = useDeleteBatch();
  const { checkPermission, hasPermission } = usePermissions();
  const { hasAnyTemplate } = useTenantDisplayTemplates();
  // Task 13 (WU-2.3) — cap-gated "Send note" doc-row action. Affordance
  // only; server is authoritative on supervisor.direct_note.
  const { user } = useAuth();
  const canDirectNote = canSendDirectNote(user);
  const [noteTarget, setNoteTarget] = useState<
    { id: string; name: string | null; documentId?: string; documentLabel?: string } | null
  >(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  // R-224 — label-allocation gets its own banner. Routing allocate errors
  // through exportMessage offered a misleading "Retry export" button on a
  // batch the operator never tried to export.
  const [allocateMessage, setAllocateMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  // Must stay with the other hooks ABOVE the early returns below — a useState
  // placed after the isLoading/error guards changes the hook count between the
  // loading and loaded renders and crashes the page with React #310 (R-81).
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // R-174 — Reprocess OCR is destructive (clears ALL operator-keyed state).
  // Gate it behind a confirm modal so a misclick can't silently nuke a
  // batch's keying work.
  const [confirmReprocessOpen, setConfirmReprocessOpen] = useState(false);
  // R-184-addendum — admin re-allocate display label confirm gate.
  const [confirmAllocateOpen, setConfirmAllocateOpen] = useState(false);
  const [isAllocatingLabel, setIsAllocatingLabel] = useState(false);
  // R-235 — "Move to step…" supervisor/admin action.
  const [moveStepOpen, setMoveStepOpen] = useState(false);
  // R-247 5b-3 — per-doc busy state for the "Reclassify (mis-identified)" inline action.
  const [reclassifyingDocId, setReclassifyingDocId] = useState<string | null>(null);

  // Populate permission cache on mount — `hasPermission` is sync/cache-only.
  useEffect(() => {
    void checkPermission("supervisor.batch_checkin");
    // R-184-addendum — gate the re-allocate display-label action.
    void checkPermission("batch_manage");
    // R-235 — gate the "Move to step…" action.
    void checkPermission("supervisor.ops_console");
    void checkPermission("admin");
    // R-247 5b-3b — gate the inline "Reclassify (mis-identified)" control.
    void checkPermission("reclassify-doc");
  }, [checkPermission]);

  // R-247 5b-3b — without `reclassify-doc` the POST returns 403; hide the
  // control rather than stranding the operator on a "Reclassify failed" toast.
  const canReclassify = hasPermission("reclassify-doc");

  // F-085 — the detail payload carries the raw docs, so derive assignedCount
  // for the header's workability phrase with the SAME shared predicate the
  // list endpoint uses (isClaimedDocStatus), memoized so the 8s poll doesn't
  // rescan on unrelated re-renders. Must stay above the early returns (hook).
  const assignedCount = useMemo(
    () => (batch?.documents ?? []).filter((d) => isClaimedDocStatus(d.status)).length,
    [batch?.documents],
  );

  // E-057 follow-on — THIS batch's docs stuck at export in an error state
  // (the queue row's badge is job-type-scoped across all batches; this is
  // the batch-scoped rollup). Uses the shared lifecycle predicate so the
  // count agrees with the queue lane badge (queue-manager filters export
  // docs by statusesForState("error"), the same set deriveDocState reads).
  const exportFailedCount = useMemo(
    () =>
      (batch?.documents ?? []).filter(
        (d) => d.currentStep === "export" && deriveDocState(d.status) === "error",
      ).length,
    [batch?.documents],
  );

  if (!id) {
    return <div className="admin-error">No batch selected</div>;
  }

  // R-182 — initial-load skeleton only. `isLoading` is one-way (never flips
  // back to true on the 8s poll), so gating on `isLoading && !batch` shows the
  // skeleton ONLY before the first fetch resolves. Background refreshes keep
  // the detail view mounted; `isRefreshing` drives a subtle non-blocking cue.
  if (isLoading && !batch) {
    return (
      <div className="admin-page">
        <div className="admin-loading"><div className="loading-spinner" /></div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="admin-page">
        <div className="admin-error">Error: {error ?? "Not found"}</div>
      </div>
    );
  }

  const rescanRequests = batch.rescanRequests ?? [];
  const isHybrid = batch.processingMode === "hybrid";
  const isBatchMode = batch.processingMode === "batch" || isHybrid;
  // R-264 5e-2 (W3) — show the reconciliation panel for ALL processing modes
  // (not just hybrid) whenever the batch is in a reconciliation or balance-review
  // state. Previously only hybrid was covered; balance_review (advisory park) is
  // new and applies to flow/batch modes as well.
  const showReconciliation =
    batch.status === "reconciliation_failed" ||
    batch.status === "reconciliation" ||
    batch.status === "balance_review";

  // `assignedTo !== null` is the lock proxy — BatchDetail payload has no dedicated lockCount.
  const batchHasActiveLocks = batch.documents.some((d) => d.assignedTo !== null);
  // R-235 — count documents currently locked to an operator for the active-operator warning.
  const activeOperatorCount = batch.documents.filter((d) => d.assignedTo !== null).length;
  const canSupervise = hasPermission("supervisor.batch_checkin");
  // R-235 — supervisor or admin may move the batch to a different pipeline step.
  const canMove = hasPermission("supervisor.ops_console") || hasPermission("admin");
  // R-184-addendum — show the re-allocate action only when the batch has no
  // frozen label, a template exists for the tenant/JT (so allocation would
  // actually produce one), and the operator can manage batches.
  const canAllocateLabel =
    batch.customDisplayLabel == null && hasAnyTemplate && hasPermission("batch_manage");

  // R-155 — derive the auto-export display state. Pre-R-155 API responses
  // omit these fields, so default to the conservative "manual required"
  // behaviour (matches the existing prominent-buttons experience).
  const effectiveManualRelease = batch.effectiveManualRelease ?? true;
  const activeExportDestinationCount = batch.activeExportDestinationCount ?? 0;
  const hasActiveDestinations = activeExportDestinationCount > 0;
  // Auto-mode is "active" (will fire on export-ready) only when the gate is
  // off AND a destination exists. With no destination, the auto-path falls
  // back to the manual flow per the R-145 pre-flight contract — so we show
  // the manual buttons prominently and surface the "no destinations" gap.
  const autoExportActive = !effectiveManualRelease && hasActiveDestinations;

  const handleRelease = async () => {
    setExportMessage(null);
    try {
      await release(batch.id);
      refresh();
    } catch (e) {
      // R-145 — surface the server's actionable reason (e.g. "No active
      // export destinations configured") via the same exportMessage banner
      // pattern as handleExport / handleConfirmDelete. Without this catch
      // the ApiError bubbled as an uncaught promise rejection to the
      // console and the operator got no feedback.
      setExportMessage({
        type: "error",
        text: getApiErrorMessage(e, { fallback: "Release failed" }),
      });
    }
  };

  // R-174 — clicking Reprocess opens the confirm modal; the actual API
  // call only fires from handleConfirmReprocess once the operator accepts
  // the "discard keyed values" warning.
  const handleReprocess = () => setConfirmReprocessOpen(true);

  const handleConfirmReprocess = async () => {
    setConfirmReprocessOpen(false);
    setExportMessage(null);
    try {
      const result = await reprocess(batch.id);
      // R-174 — surface the count of keyed values that were just discarded
      // (R-176 made reprocess destructive on purpose; this toast tells the
      // operator the cost so they can trust the action did what it said).
      const cleared = result?.clearedFieldCount ?? 0;
      const docCount = result?.documentCount ?? batch.documents.length;
      setExportMessage({
        type: "success",
        text:
          cleared > 0
            ? `Reset ${docCount} docs (cleared ${cleared} keyed values)`
            : `Reset ${docCount} docs`,
      });
      refresh();
    } catch (e) {
      setExportMessage({
        type: "error",
        text: getApiErrorMessage(e, { fallback: "Reprocess failed" }),
      });
    }
  };

  const handleDelete = () => setConfirmDeleteOpen(true);

  const handleConfirmDelete = async () => {
    setConfirmDeleteOpen(false);
    try {
      await deleteBatch(batch.id);
      navigate("/batches");
    } catch (e) {
      setExportMessage({
        type: "error",
        text: getApiErrorMessage(e, { fallback: "Delete failed" }),
      });
    }
  };

  // R-184-addendum — re-allocate the frozen display label for a batch stuck
  // with customDisplayLabel = null (R-184 silent-swallow recovery, or the
  // pre-R-167 backfill case). Confirm gate so the counter advance is deliberate.
  const handleAllocateLabel = () => setConfirmAllocateOpen(true);

  const handleConfirmAllocateLabel = async () => {
    setConfirmAllocateOpen(false);
    setIsAllocatingLabel(true);
    setAllocateMessage(null);
    try {
      await api.post(`/batches/${batch.id}/allocate-display-label`);
      setAllocateMessage({ type: "success", text: "Display label allocated" });
      refresh();
    } catch (e) {
      setAllocateMessage({
        type: "error",
        text: getApiErrorMessage(e, { fallback: "Display label allocation failed" }),
      });
    } finally {
      setIsAllocatingLabel(false);
    }
  };

  // R-261 — clear an open rescan request on this batch.
  const handleClearRescan = async (requestId: string) => {
    try {
      await api.post(`/batches/${id}/rescan-requests/${requestId}/clear`, {});
      refresh();
    } catch {
      toast.error("Failed to clear rescan request");
    }
  };

  // R-247 5b-3 — flag a doc that was wrongly fingerprint-auto-routed.
  // The endpoint (Task 4) locks the doc, clears fields, parks it at `classify`,
  // reactivates the batch, audits, and best-effort signals OCR.
  const handleReclassifyMisfire = async (docId: string) => {
    setReclassifyingDocId(docId);
    try {
      await api.post(`/documents/${docId}/reclassify-misfire`, {});
      refresh();
    } catch {
      // api-client handles 401 (bounce to login). Any other failure surfaces a
      // toast matching the EscalationQueue / EscalationContextPanel surfaces.
      toast.error("Reclassify failed");
    } finally {
      setReclassifyingDocId(null);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportMessage(null);
    try {
      await api.post(`/batches/${batch.id}/release`);
      setExportMessage({ type: "success", text: "Export triggered successfully" });
      refresh();
    } catch (e) {
      // R-88 — surface the server's actionable reason (e.g. "Release is
      // only for batch/hybrid mode") instead of the bare ApiError message
      // "API error 400". Mirrors handleConfirmDelete.
      setExportMessage({
        type: "error",
        text: getApiErrorMessage(e, { fallback: "Export failed" }),
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="admin-page">
      <ConfirmModal
        open={confirmDeleteOpen}
        title={`Delete batch "${batch.name}"?`}
        description="Removes the batch and all its documents. This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
      {/* R-174 — destructive Reprocess OCR confirm. Backstops R-176, which
          made reprocess clear every operator-keyed value on the batch
          before re-enqueuing OCR. Without this gate a misclick would
          silently discard the keying work. */}
      <ConfirmModal
        open={confirmReprocessOpen}
        title="Reprocess OCR — discard keyed values?"
        description="All operator-keyed values, verifications, and overrides for this batch will be cleared. Documents will re-run OCR from scratch. This is supervisor-only and cannot be undone."
        confirmLabel="Reprocess and discard"
        destructive
        onConfirm={handleConfirmReprocess}
        onCancel={() => setConfirmReprocessOpen(false)}
      />
      {/* R-184-addendum — re-allocate display label confirm. Explains that this
          allocates the next counter value(s) from the template and freezes the
          result as the batch's label (and cascades to its documents). */}
      <ConfirmModal
        open={confirmAllocateOpen}
        title="Generate display label?"
        description="This allocates the next counter value(s) from the configured template and stamps the result as this batch's display label (and its documents' labels). The label is then frozen as usual."
        confirmLabel="Generate label"
        onConfirm={handleConfirmAllocateLabel}
        onCancel={() => setConfirmAllocateOpen(false)}
      />
      {/* R-235 — "Move to step…" modal: supervisor/admin only. Mounted here
          (outside the guard below) so React hook count stays constant. */}
      <MoveStepModal
        batchId={batch.id}
        activeOperatorCount={activeOperatorCount}
        open={moveStepOpen}
        onClose={() => setMoveStepOpen(false)}
        onSuccess={() => {
          setMoveStepOpen(false);
          refresh();
        }}
      />
      {/* Task 13 (WU-2.3) — supervisor compose dialog for the doc-row "Send
          note" action below. Falls back to the recipient picker when the
          doc has no assignee (noteTarget.id === ""). */}
      {noteTarget && (
        <ComposeNoteDialog
          presetRecipient={noteTarget.id ? { id: noteTarget.id, name: noteTarget.name } : null}
          documentId={noteTarget.documentId}
          documentLabel={noteTarget.documentLabel}
          onClose={() => setNoteTarget(null)}
          onSent={() => setNoteTarget(null)}
        />
      )}
      <div className="admin-header">
        <button className="admin-back" onClick={() => navigate("/batches")}>
          &larr; Batches
        </button>
        <div className="batch-detail-header">
          {/* R-132 — primary identifier line shows the structured displayId
              (e.g. NOW_20260523_0001) or a muted `B-<cuid-prefix>` fallback
              for legacy batches. The free-form name renders as the subtitle. */}
          <div className="batch-detail-id-block">
            <h1
              className={
                hasStructuredDisplayId(batch)
                  ? "admin-title batch-detail-display-id"
                  : "admin-title batch-detail-display-id batch-detail-display-id--fallback"
              }
              data-testid="batch-detail-display-id"
            >
              {/* R-167 WU-7 — customDisplayLabel ?? displayId ?? cuid fallback.
                  Frozen at create time; no per-request template render. */}
              {renderBatchLabel(batch)}
            </h1>
            {batch.customDisplayLabel == null && hasAnyTemplate && (
              <span
                className="batch-display-id-info"
                data-testid="batch-detail-display-id-info"
                title="Custom label not assigned (batch predates current template config)."
                aria-label="Custom label not assigned"
              >
                ℹ
              </span>
            )}
            <span className="batch-detail-name-sub" data-testid="batch-detail-name-sub">
              {batch.name}
            </span>
          </div>
          {/* R-133 / F-048 / F-085 — canonical 4-state badge (derived from the
              doc breakdown) composed with workability, so the detail header
              reads the SAME "<state> — <workability>" phrase as the list row.
              assignedCount is derived above via the shared isClaimedDocStatus
              predicate (same source as the list endpoint's computation). */}
          <BatchStatusBadge
            breakdown={batch.stageBreakdown}
            workability={workItemStatus({
              processingMode: batch.processingMode,
              currentStep: batch.currentStep,
              assignedCount,
            })}
          />
          {/* E-057 — per-batch export-error rollup; clicks through to Export
              History filtered to this batch's failed (dead-letter) records. */}
          {exportFailedCount > 0 && (
            <Link
              to={`/admin/export-history?batchId=${encodeURIComponent(batch.id)}&status=dead_letter`}
              className="batch-export-failed"
              data-testid="batch-export-failed"
              title={`${exportFailedCount} document${exportFailedCount === 1 ? "" : "s"} of this batch failed export — open Export History filtered to this batch's failed records`}
            >
              {exportFailedCount} doc{exportFailedCount === 1 ? "" : "s"} failed export
            </Link>
          )}
          {/* R-182 / R-228 — subtle non-blocking cue for the 8s background
              poll. ALWAYS mounted so it reserves its flex slot (+ gap); only
              its opacity toggles via data-active. The poll therefore never
              adds/removes a flex child, so the header does not reflow. */}
          <span
            className="batch-detail-refreshing"
            data-active={isRefreshing ? "true" : "false"}
            role="status"
            aria-live="polite"
            data-testid="batch-detail-refreshing"
          >
            Refreshing…
          </span>
        </div>
        {/* R-133 — header breakdown line; quick view of where the batch's
            docs are distributed across stages. */}
        <BatchStageBreakdown breakdown={batch.stageBreakdown} />
      </div>

      <div className="batch-detail-meta">
        <div>
          <div className="batch-detail-meta-label">Source</div>
          <span style={{ textTransform: "capitalize" }}>{batch.source}</span>
        </div>
        <div>
          <div className="batch-detail-meta-label">Job Type</div>
          <span>{batch.jobTypeName}</span>
        </div>
        <div>
          <div className="batch-detail-meta-label">Mode</div>
          <span style={{ textTransform: "capitalize" }}>{batch.processingMode}</span>
        </div>
        <div>
          <div className="batch-detail-meta-label">Documents</div>
          <span>{batch.documentCount}</span>
        </div>
      </div>

      {/* Pipeline Progress
          R-166 — show a real percentage above the stage strip. Pre-fix the
          strip only rendered per-stage doc counts, leaving the operator to
          eyeball "is this batch 10% or 90% done?" from the cell highlights.
          The percentage gives partial credit per stage (see
          computeBatchProgress) so a doc parked at Export reads as
          high-progress instead of "0% nothing has happened yet." */}
      {batch.pipelineProgress.length > 0 && (
        <>
          {(() => {
            const progress = computeBatchProgress(batch.documents);
            return (
              <div
                className="batch-pipeline-progress-header"
                data-testid="pipeline-progress-percent"
              >
                <div className="batch-pipeline-progress-label">
                  Pipeline progress
                </div>
                <div className="batch-pipeline-progress-bar">
                  <div
                    className="batch-pipeline-progress-bar-fill"
                    style={{ width: `${progress.percentComplete}%` }}
                  />
                </div>
                <div
                  className="batch-pipeline-progress-value"
                  data-testid="pipeline-progress-value"
                >
                  {progress.percentComplete}%
                </div>
              </div>
            );
          })()}
          <div className="batch-pipeline" data-testid="pipeline-progress">
            {batch.pipelineProgress.map((step) => (
              <div
                key={step.step}
                className={`batch-pipeline-step ${step.count > 0 ? "batch-pipeline-step--active" : ""}`}
              >
                <div className="batch-pipeline-step-label">{step.label}</div>
                <div className="batch-pipeline-step-count">{step.count}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* R-155 — auto-export status banner. Always shown so operators know
          whether they need to take action; the tone changes with state:
            • auto-export active → green hint "fires automatically"
            • manual required    → neutral "click Release for Export"
            • no destinations    → warning "no destinations configured" */}
      {isBatchMode && (
        <div
          className={`batch-auto-export-banner batch-auto-export-banner--${
            !hasActiveDestinations
              ? "warning"
              : autoExportActive
              ? "auto"
              : "manual"
          }`}
          data-testid="auto-export-banner"
        >
          {!hasActiveDestinations ? (
            <>
              <strong>No active export destinations.</strong>{" "}
              Add one in Form Config → Export tab before this batch can be
              released.
            </>
          ) : autoExportActive ? (
            <>
              <strong>Auto-release on.</strong>{" "}
              This batch fires the export automatically as soon as all
              documents are keyed and validated. Manual buttons remain
              available as a fallback.
            </>
          ) : (
            <>
              <strong>Manual release required.</strong>{" "}
              Click "Release for Export" once all documents are keyed and
              validated.
            </>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="batch-actions" data-testid="batch-actions">
        {isBatchMode && (
          <button
            className={`batch-action-btn ${
              autoExportActive
                ? "batch-action-btn--secondary"
                : "batch-action-btn--primary"
            }`}
            onClick={handleRelease}
            disabled={isReleasing}
            data-testid="release-btn"
            title={
              autoExportActive
                ? "Auto-release is on — this is a manual fallback."
                : undefined
            }
          >
            {isReleasing ? "Releasing..." : "Release for Export"}
          </button>
        )}
        <button
          className={`batch-action-btn ${
            autoExportActive
              ? "batch-action-btn--secondary"
              : "batch-action-btn--primary"
          }`}
          onClick={handleExport}
          disabled={isExporting}
          data-testid="export-btn"
          title={
            autoExportActive
              ? "Auto-release is on — this is a manual fallback."
              : undefined
          }
        >
          {isExporting ? "Exporting..." : "Export"}
        </button>
        <button
          className="batch-action-btn batch-action-btn--secondary"
          onClick={handleReprocess}
          disabled={isReprocessing}
          data-testid="reprocess-btn"
        >
          {isReprocessing ? "Reprocessing..." : "Reprocess OCR"}
        </button>
        {/* R-184-addendum — recover a batch stuck with a null custom label. */}
        {canAllocateLabel && (
          <button
            type="button"
            className="batch-action-btn batch-action-btn--secondary"
            onClick={handleAllocateLabel}
            disabled={isAllocatingLabel}
            data-testid="allocate-label-btn"
            title="This batch has no custom display label. Generate one from the configured template."
          >
            {isAllocatingLabel ? "Generating…" : "Generate display label"}
          </button>
        )}
        <button
          className="batch-action-btn batch-action-btn--secondary"
          onClick={() => navigate("/admin/export-history")}
          data-testid="export-history-btn"
        >
          Export History
        </button>
        {canSupervise && batchHasActiveLocks && (
          <button
            type="button"
            className="batch-action-btn batch-action-btn--secondary"
            onClick={() => setCheckInOpen(true)}
            data-testid="supervisor-checkin-btn"
          >
            Check In Batch (Supervisor)
          </button>
        )}
        {/* R-235 — supervisor/admin move-to-step. Gated on ops_console or admin. */}
        {canMove && (
          <button
            type="button"
            className="batch-action-btn batch-action-btn--secondary"
            onClick={() => setMoveStepOpen(true)}
            data-testid="move-step-btn"
          >
            Move to step…
          </button>
        )}
        {/* Stuck-batch remediation — visible only for terminal states so an
            admin can't accidentally cancel in-flight keying work. */}
        {(batch.status === "uploading" || batch.status === "failed") && (
          <button
            type="button"
            className="batch-action-btn batch-action-btn--danger"
            onClick={handleDelete}
            disabled={isDeleting}
            data-testid="delete-batch-btn"
          >
            {isDeleting ? "Deleting…" : "Delete batch"}
          </button>
        )}
      </div>

      <BatchCheckInModal
        batchId={batch.id}
        open={checkInOpen}
        onClose={() => setCheckInOpen(false)}
        onSuccess={() => {
          setCheckInOpen(false);
          refresh();
        }}
      />

      {/* Export feedback */}
      {exportMessage && (
        <div
          className={`batch-export-msg batch-export-msg--${exportMessage.type}`}
          data-testid="export-message"
          role={exportMessage.type === "error" ? "alert" : "status"}
        >
          <span className="batch-export-msg-text">{exportMessage.text}</span>
          {exportMessage.type === "error" && (
            <button
              type="button"
              className="batch-export-msg-retry"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? "Retrying…" : "Retry export"}
            </button>
          )}
        </div>
      )}

      {/* R-224 — label-allocation feedback. Dedicated banner with no
          "Retry export" affordance; an allocation error offers only a
          "Try again" that re-opens the allocate confirm. */}
      {allocateMessage && (
        <div
          className={`batch-export-msg batch-export-msg--${allocateMessage.type}`}
          data-testid="allocate-message"
          role={allocateMessage.type === "error" ? "alert" : "status"}
        >
          <span className="batch-export-msg-text">{allocateMessage.text}</span>
          {allocateMessage.type === "error" && (
            <button
              type="button"
              className="batch-export-msg-retry"
              onClick={handleAllocateLabel}
              disabled={isAllocatingLabel}
            >
              Try again
            </button>
          )}
          {/* R-224 code-review — both success and error states get an explicit
              dismiss. On success the "Generate display label" button unmounts
              after refresh, so without this the banner would linger forever. */}
          <button
            type="button"
            className="batch-export-msg-retry"
            onClick={() => setAllocateMessage(null)}
            data-testid="allocate-message-dismiss"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Reconciliation */}
      {showReconciliation && (
        <ReconciliationPanel batchId={batch.id} processingMode={batch.processingMode} onRelease={refresh} />
      )}

      {/* SP2 §8 (Task 11) — remittance-only, renders nothing for standard
          batches (TransactionsPanel returns null when surface !== "remittance"
          or there are no transactions). */}
      <TransactionsPanel
        batchId={batch.id}
        surface={batch.surface ?? "standard"}
        transactions={batch.transactions ?? []}
        onChanged={refresh}
      />

      {/* R-261 — Rescan section: rendered above the document table when there
          are open rescan requests. `rescanRequests` may be absent on pre-R-261
          API responses; the `?? []` guard (above) keeps the section hidden in that case. */}
      {rescanRequests.length > 0 && (
        <section className="rescan-section">
          <h3>Rescan requested</h3>
          {rescanRequests.map((r) => (
            <div key={r.id} className="rescan-card">
              <div className="rescan-card-body">
                <span className="rescan-card-doc">{r.context?.filename ?? r.documentId ?? "—"}</span>
                {r.context?.pageNumbers && (
                  <span> · pages {r.context.pageNumbers.join(", ")}</span>
                )}
                {r.note && <p className="rescan-card-note">{r.note}</p>}
                <span className="rescan-card-meta">
                  {r.requestedBy} · {new Date(r.createdAt).toLocaleString()}
                </span>
              </div>
              <button type="button" onClick={() => handleClearRescan(r.id)}>Clear</button>
            </div>
          ))}
        </section>
      )}

      {/* Document Table */}
      <table className="batch-doc-table" data-testid="document-table">
        <thead>
          <tr>
            <th>Document</th>
            <th>Classification</th>
            <th>Current Step</th>
            <th>Status</th>
            <th>Assigned To</th>
            <th>Priority</th>
          </tr>
        </thead>
        <tbody>
          {batch.documents.map((doc) => {
            const keyable = ["key", "verify", "qc", "supervisor"].includes(doc.currentStep);
            return (
            <tr
              key={doc.id}
              onClick={() => keyable && navigate(`/verify/${doc.id}`)}
              style={{ cursor: keyable ? "pointer" : "default", opacity: keyable ? 1 : 0.6 }}
              title={keyable ? undefined : "Not in a keying stage"}
            >
              <td>
                {/* R-132 — primary doc identifier (e.g. NOW_20260523_0001_01)
                    with the original filename rendered as the muted subtitle.
                    Legacy docs fall back to `D-<cuid-prefix>`. */}
                <span
                  className={
                    doc.displayId
                      ? "batch-display-id"
                      : "batch-display-id batch-display-id--fallback"
                  }
                  data-testid="doc-display-id"
                >
                  {/* R-167 WU-7 — customDisplayLabel ?? displayId ?? cuid. */}
                  {renderDocumentLabel(doc)}
                </span>
                <span className="batch-name-sub" data-testid="doc-filename-sub">
                  {doc.filename}
                </span>
              </td>
              <td>
                {/* E-026 — a job type with classificationMode "off" never
                    resolves a classification (it's not attempted; extraction
                    goes straight to the job's default form), so a null
                    `classification` here is expected, not a problem. Only
                    jobs where classification IS attempted show "Unclassified". */}
                {doc.classification ?? (
                  batch.jobTypeClassificationMode === "off" ? (
                    <span
                      className="batch-doc-unassigned"
                      data-testid="doc-classification-off"
                    >
                      — (single form)
                    </span>
                  ) : (
                    <span
                      className="batch-doc-unassigned"
                      data-testid="doc-classification-unclassified"
                    >
                      Unclassified
                    </span>
                  )
                )}
                {/* R-247 5b-3b — show the unified reclassify control for
                    fingerprint mis-fires AND any keyer-escalated mis-identified
                    doc (escalationContext.category === "misidentified_type"). */}
                {canReclassify &&
                  (doc.classificationSource === "fingerprint" ||
                    doc.escalationContext?.category === "misidentified_type") && (
                  <button
                    type="button"
                    className="batch-action-btn batch-action-btn--secondary batch-doc-misfire-btn"
                    disabled={reclassifyingDocId === doc.id}
                    onClick={(e) => { e.stopPropagation(); handleReclassifyMisfire(doc.id); }}
                    data-testid={`reclassify-misfire-${doc.id}`}
                    title="Flag this document as mis-identified and return it to classify"
                  >
                    {reclassifyingDocId === doc.id ? "…" : "Reclassify (mis-identified)"}
                  </button>
                )}
                {/* Task 13 (WU-2.3) — presets the recipient when the doc is
                    assigned; falls back to the picker when unassigned. */}
                {canDirectNote && (
                  <button
                    type="button"
                    className="batch-action-btn batch-action-btn--secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNoteTarget({
                        id: doc.assignedTo ?? "",
                        name: doc.assignedToName,
                        documentId: doc.id,
                        documentLabel: doc.filename,
                      });
                    }}
                    data-testid={`send-note-${doc.id}`}
                  >
                    Send note
                  </button>
                )}
              </td>
              <td>{formatStep(doc.currentStep)}</td>
              <td>
                {/* F-085 — the shared 4-state doc vocabulary (Ready / In
                    progress / Needs attention / Complete), NOT the raw
                    doc.status word, so this table speaks the same language
                    as the badge, breakdown, and Queue Dashboard. */}
                <DocStateBadge status={doc.status} />
                {/* R-131 — surface the captured OCR/processing error inline
                    so admins don't have to leave BatchDetail to find out
                    why a doc transitioned to `error`. Renders only when the
                    doc actually failed AND has a reason to show. */}
                {doc.status === "error" && (doc.holdReason || doc.errorCode) && (
                  <DocErrorPopover
                    documentId={doc.id}
                    holdReason={doc.holdReason}
                    errorCode={doc.errorCode}
                    errorDetails={doc.errorDetails}
                  />
                )}
              </td>
              <td>
                {doc.assignedToName ?? (
                  <span className="batch-doc-unassigned">Unassigned</span>
                )}
              </td>
              <td>{doc.priority}</td>
            </tr>
            );
          })}
          {batch.documents.length === 0 && (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", padding: "20px", color: "var(--color-text-muted, #737373)" }}>
                No documents in this batch.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
